import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faArrowRight,
  faPhone,
  faSyringe,
  faUserDoctor,
  faFileMedical,
  faClipboardList,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';

const PAGE_URL = 'https://lifeandwellnesscenter.org/immigration-medical-exam-odessa-tx';
const PAGE_TITLE =
  'USCIS Immigration Medical Exam (Form I-693) in Odessa, TX | Life & Wellness Center';
const PAGE_DESCRIPTION =
  'USCIS-designated civil surgeon in Odessa, TX performing the complete immigration medical exam (Form I-693) for green card applicants — physical exam, TB and lab testing, and required vaccinations available on-site. Se habla español. Monday–Friday. Book your I-693 exam today.';

const examIncludes = [
  {
    icon: faFileMedical,
    title: 'Medical history & physical exam',
    text: 'A review of your health history and a complete physical examination by our civil surgeon.'
  },
  {
    icon: faClipboardList,
    title: 'Tuberculosis (TB) testing',
    text: 'An IGRA blood test for tuberculosis, required for all applicants 2 years of age and older.'
  },
  {
    icon: faClipboardList,
    title: 'Blood tests (syphilis & gonorrhea)',
    text: 'Serologic testing for syphilis (ages 18–44) and testing for gonorrhea, as required by USCIS.'
  },
  {
    icon: faUserDoctor,
    title: 'Screening for public-health conditions',
    text: 'Evaluation for communicable diseases of public-health significance, and for mental/physical disorders and drug or alcohol concerns.'
  },
  {
    icon: faSyringe,
    title: 'Vaccination review & administration',
    text: 'We review your immunization records and administer any required immigration vaccines on-site, then complete Part 10 of the form.'
  },
  {
    icon: faFileMedical,
    title: 'Completed & sealed Form I-693',
    text: 'Your civil surgeon signs and seals the completed Form I-693 for submission to USCIS.'
  }
];

const serviceChecklist = [
  'Performed by a USCIS-designated civil surgeon',
  'Complete physical examination',
  'Review of your vaccination records',
  'Required immigration vaccines available on-site',
  'TB testing and required lab work',
  'Completed and sealed Form I-693 for USCIS'
];

const whatToBring = [
  'A valid government-issued photo ID or passport',
  'Your immunization / vaccination records (bring everything you have)',
  'Form I-693 (current USCIS edition), if you already have it',
  'A list of your current medications and any relevant prior medical records',
  'Glasses or contact lenses, if you wear them'
];

const whyChooseUs = [
  'USCIS-designated civil surgeon — authorized to complete and certify Form I-693',
  'Complete exam, TB/lab testing, and required vaccinations on-site — in one visit',
  'Se habla español — Spanish-speaking staff to assist you',
  'Conveniently located in Odessa, TX',
  'Monday–Friday appointments, 9:00 AM – 5:00 PM'
];

const faqs = [
  {
    q: 'What is Form I-693 and who needs it?',
    a: 'Form I-693 (Report of Immigration Medical Examination and Vaccination Record) establishes that you are not inadmissible to the United States on health-related grounds. It is generally required for applicants filing Form I-485 to adjust status to lawful permanent resident (green card).'
  },
  {
    q: 'Who is allowed to perform the I-693 exam?',
    a: 'Only a USCIS-designated civil surgeon can complete and sign Form I-693. Our clinic is a designated civil surgeon, so your exam can be performed and certified right here in Odessa.'
  },
  {
    q: 'Should I open the sealed envelope?',
    a: 'No. Do not open the sealed envelope your civil surgeon gives you. If you are filing your application online, follow USCIS instructions to open it only to upload the form, and keep the original envelope until USCIS makes a final decision. If filing by mail, submit it in the original sealed envelope.'
  },
  {
    q: 'How long is the I-693 valid?',
    a: 'A Form I-693 properly signed by a civil surgeon on or after November 1, 2023 does not expire while the application it was submitted with is still pending (it is no longer valid if that application is withdrawn or denied). Forms signed before that date retain evidentiary value for two years from the signature date.'
  },
  {
    q: 'Do you provide the required vaccinations?',
    a: 'Yes. We administer the required immigration vaccinations on-site, so in most cases your exam and vaccinations can be completed in a single visit. Please bring any vaccination records you already have.'
  },
  {
    q: '¿Hablan español? Do you offer the immigration exam in Spanish?',
    a: 'Yes — se habla español. Our Spanish-speaking staff can assist you throughout your immigration medical exam, so you can ask questions and understand every step comfortably.'
  },
  {
    q: 'What are your hours and where are you located?',
    a: 'We see immigration exam patients by appointment Monday through Friday, 9:00 AM to 5:00 PM, at 6010 E Highway 191, Suite 240, Odessa, TX. Call (432) 653-5487 to schedule.'
  }
];

const ImmigrationExam = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
      return el;
    };

    const desc = setMeta('name', 'description', PAGE_DESCRIPTION);
    const ogTitle = setMeta('property', 'og:title', PAGE_TITLE);
    const ogDesc = setMeta('property', 'og:description', PAGE_DESCRIPTION);
    const ogUrl = setMeta('property', 'og:url', PAGE_URL);

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      desc.setAttribute(
        'content',
        'Life and Wellness Center - Premium mental wellness, behavioral health, and holistic care designed to help you thrive.'
      );
      ogTitle.remove();
      ogDesc.remove();
      ogUrl.remove();
      if (createdCanonical && canonical) canonical.remove();
    };
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    name: 'Life and Wellness Center',
    url: PAGE_URL,
    telephone: '+1-432-653-5487',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '6010 E Highway 191, Suite 240',
      addressLocality: 'Odessa',
      addressRegion: 'TX',
      addressCountry: 'US'
    },
    openingHours: 'Mo-Fr 09:00-17:00',
    availableLanguage: ['English', 'Spanish'],
    areaServed: { '@type': 'City', name: 'Odessa, Texas' },
    availableService: {
      '@type': 'MedicalProcedure',
      name: 'USCIS Immigration Medical Exam (Form I-693)',
      description:
        'Official U.S. immigration medical examination and vaccination record (Form I-693) completed by a USCIS-designated civil surgeon — including the physical examination, TB and required lab testing, vaccination record review, required vaccines administered on-site, and a completed, sealed Form I-693.'
    }
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />

      {/* Hero */}
      <section className="page-section bg-light" style={{ padding: '64px 0 44px' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={9}>
              <span className="section-eyebrow">USCIS-Designated Civil Surgeon · Odessa, TX · Se habla español</span>
              <h1
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(2rem, 4.5vw, 3.1rem)',
                  lineHeight: 1.15,
                  color: 'var(--primary-dark)',
                  margin: '14px 0 18px'
                }}
              >
                USCIS Immigration Medical Exam <span style={{ color: 'var(--primary-color)' }}>(Form I-693)</span> in Odessa, TX
              </h1>
              <p style={{ fontSize: '18px', color: 'var(--text-light)', lineHeight: 1.7, marginBottom: '26px' }}>
                Get your official U.S. immigration medical exam completed by a USCIS-designated civil surgeon —
                including any required vaccinations, all in one convenient visit here in Odessa.
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                <Button as={Link} to="/appointment" variant="primary" size="lg" className="btn-pill">
                  Book Your I-693 Exam <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Button>
                <Button
                  href="tel:+14326535487"
                  variant="outline-primary"
                  size="lg"
                  className="btn-pill"
                >
                  <FontAwesomeIcon icon={faPhone} className="me-2" /> (432) 653-5487
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Scannable "what's included" checklist + NAP (SEO / at-a-glance) */}
      <section className="page-section" style={{ paddingTop: '44px', paddingBottom: '10px' }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              <div
                style={{
                  background: 'linear-gradient(135deg, #eef3ec, #e3ede4)',
                  border: '1px solid rgba(90, 138, 115, 0.25)',
                  borderRadius: '16px',
                  padding: '26px 30px'
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: 'var(--primary-dark)',
                    fontSize: '1.4rem',
                    marginBottom: '18px'
                  }}
                >
                  Your complete USCIS I-693 exam — everything in one visit
                </h2>
                <Row className="g-2">
                  {serviceChecklist.map((item, i) => (
                    <Col md={6} key={i}>
                      <div className="d-flex align-items-start" style={{ color: 'var(--text-color)' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" style={{ color: 'var(--primary-color)' }} />
                        <span style={{ fontSize: '15px', lineHeight: 1.5 }}>{item}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
                <p style={{ margin: '18px 0 0', fontSize: '14px', color: 'var(--primary-dark)', fontWeight: 600 }}>
                  Odessa, TX &middot; Monday–Friday, 9:00 AM–5:00 PM &middot; Se habla español &middot; (432) 653-5487
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What is it */}
      <section className="page-section" style={{ paddingTop: '44px' }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-dark)', marginBottom: '16px' }}>
                What is the Form I-693 immigration medical exam?
              </h2>
              <p style={{ fontSize: '17px', lineHeight: 1.8, color: 'var(--text-color)' }}>
                Form I-693, the <em>Report of Immigration Medical Examination and Vaccination Record</em>, is
                required by U.S. Citizenship and Immigration Services (USCIS) to show that an applicant is not
                inadmissible to the United States on health-related grounds. It is most commonly needed by
                applicants filing <strong>Form I-485</strong> to adjust status to lawful permanent resident (a green card).
              </p>
              <p style={{ fontSize: '17px', lineHeight: 1.8, color: 'var(--text-color)' }}>
                By law, the exam must be completed and certified by a <strong>USCIS-designated civil surgeon</strong>.
                As a designated civil surgeon, our clinic can perform your exam, administer required vaccinations,
                and provide your completed, sealed Form I-693 — right here in Odessa, TX.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What the exam includes */}
      <section className="page-section bg-light">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">The Exam</span>
            <h3>What the I-693 exam includes</h3>
            <p>A thorough, USCIS-compliant evaluation — completed with care and privacy.</p>
          </div>
          <Row className="g-4">
            {examIncludes.map((item, i) => (
              <Col md={6} lg={4} key={i}>
                <div className="scard-premium" style={{ cursor: 'default' }}>
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #eaf0e8, #dde9df)',
                      display: 'grid',
                      placeItems: 'center',
                      marginBottom: '16px'
                    }}
                  >
                    <FontAwesomeIcon icon={item.icon} style={{ color: 'var(--primary-color)', fontSize: '20px' }} />
                  </div>
                  <h4 className="scard-title">{item.title}</h4>
                  <p className="scard-desc" style={{ minHeight: 'auto' }}>{item.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Vaccinations + What to bring */}
      <section className="page-section">
        <Container>
          <Row className="g-5">
            <Col lg={6}>
              <span className="section-eyebrow">Vaccinations</span>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-dark)', margin: '10px 0 16px' }}>
                Required vaccines — administered on-site
              </h3>
              <p style={{ fontSize: '17px', lineHeight: 1.8, color: 'var(--text-color)' }}>
                USCIS requires proof of certain vaccinations as part of Form I-693 (Part 10). We review the
                immunization records you bring and administer any missing vaccines that apply to you on-site —
                based on your age and the current CDC/USCIS requirements — so you can usually finish everything
                in one visit.
              </p>
              <p style={{ fontSize: '15px', color: 'var(--text-light)', lineHeight: 1.7 }}>
                Please bring every vaccination record you have; it helps us avoid repeating vaccines you’ve
                already received.
              </p>
            </Col>
            <Col lg={6}>
              <span className="section-eyebrow">Your Visit</span>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-dark)', margin: '10px 0 16px' }}>
                What to bring to your appointment
              </h3>
              <ul className="list-unstyled">
                {whatToBring.map((item, i) => (
                  <li key={i} className="d-flex align-items-start mb-3" style={{ color: 'var(--text-color)' }}>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-3 mt-1" style={{ color: 'var(--primary-color)' }} />
                    <span style={{ fontSize: '16px', lineHeight: 1.6 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Important to know */}
      <section className="page-section bg-light">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              <div
                style={{
                  background: 'var(--bg-white, #fff)',
                  border: '1px solid rgba(201, 169, 110, 0.35)',
                  borderRadius: '16px',
                  padding: '28px 30px'
                }}
              >
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#c9a96e', fontSize: '22px' }} className="me-3" />
                  <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-dark)', margin: 0 }}>
                    Important things to know
                  </h3>
                </div>
                <ul style={{ fontSize: '16px', lineHeight: 1.8, color: 'var(--text-color)', marginBottom: 0 }}>
                  <li><strong>Don’t open the sealed envelope.</strong> We seal your completed Form I-693 for USCIS. If you file online, only open it to upload the form as USCIS instructs, and keep the original envelope until your case is decided. If you file by mail, submit it sealed.</li>
                  <li><strong>Use the current form edition.</strong> USCIS accepts only the current edition of Form I-693, and the form must be signed.</li>
                  <li><strong>Validity.</strong> An I-693 signed on or after November 1, 2023 does not expire while your application is pending. It becomes invalid if that application is withdrawn or denied.</li>
                </ul>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '14px', textAlign: 'center' }}>
                Immigration medical requirements are set by USCIS and the CDC and may change. For official guidance,
                visit uscis.gov.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why choose us */}
      <section className="page-section">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Why Choose Us</span>
            <h3>Your immigration exam, made simple</h3>
          </div>
          <Row className="justify-content-center g-3" style={{ maxWidth: '860px', margin: '0 auto' }}>
            {whyChooseUs.map((item, i) => (
              <Col md={6} key={i}>
                <div className="d-flex align-items-start" style={{ color: 'var(--text-color)' }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-3 mt-1" style={{ color: 'var(--primary-color)' }} />
                  <span style={{ fontSize: '16px', lineHeight: 1.6 }}>{item}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section className="page-section bg-light">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">FAQ</span>
            <h3>Immigration exam questions</h3>
          </div>
          <div className="faq-wrap">
            {faqs.map((f, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--bg-white, #fff)',
                  border: '1px solid rgba(45, 58, 53, 0.10)',
                  borderRadius: '14px',
                  padding: '20px 22px',
                  marginBottom: '14px'
                }}
              >
                <h4 style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--primary-dark)', marginBottom: '8px' }}>
                  {f.q}
                </h4>
                <p style={{ color: 'var(--text-light)', lineHeight: 1.7, marginBottom: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="appointment-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mb-4 mb-lg-0">
              <h3>Ready to complete your I-693 exam?</h3>
              <p className="mb-0">
                Book your USCIS immigration medical exam in Odessa today — exam and required vaccinations in one visit.
                6010 E Highway 191, Suite 240, Odessa, TX &middot; Monday–Friday &middot; Se habla español.
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <Button as={Link} to="/appointment" variant="light" size="lg" className="btn-pill me-2 mb-2 mb-lg-0">
                Book Appointment
              </Button>
              <Button href="tel:+14326535487" variant="outline-light" size="lg" className="btn-pill">
                Call Us
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ImmigrationExam;
